/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import useCustomMessageError from 'src/components/Player/hooks/useCustomMessageError';
import useLoadUserPreferences from 'src/components/Player/hooks/useLoadUserPreferences';
import usePlayerIntervals from 'src/components/Player/hooks/usePlayerIntervals';
import PlayerMarkup from 'src/components/PlayerMarkup';
import Poster from 'src/components/Poster';
import useContextMenu from 'src/hooks/useContextMenu';
import usePlayback from 'src/hooks/usePlayback';
import usePlayerData from 'src/hooks/usePlayerData';
import { useSelector } from 'react-redux';
import { SELECTOR_PLAYBACK_FIRST_PLAY } from 'src/store/reducers/playback';
import usePlayerError from 'src/hooks/usePlayerError';
import useStats from 'src/hooks/useStats';
import useUI from 'src/hooks/useUI';
import { detectAndroid, detectIOS, reloadPage } from 'src/utils/browserUtils';
import useTranslation from 'next-translate/useTranslation';
import useTextTrack from 'src/components/Player/hooks/useTextTrack';
import useSubtitle from 'src/hooks/useSubtitle';
import LoadingSpinner from 'src/components/LoadingSpinner';
import IgnorePlayerEvents from 'src/components/IgnorePlayerEvents';
import { IconSize } from 'src/interfaces/Icon';
import usePlayerParams from 'src/hooks/usePlayerParams';
import classNames from 'classnames';
import useAutoplay from 'src/hooks/useAutoplay';
import OverlayError from '../../components/OverlayError';
import ContextMenu from '../../components/ContextMenu';
import BottomBarDesktop from '../BottomBarDesktop';
import ContextMenuItem from '../../components/ContextMenuItem';
import { ReloadIcon, StatsIcon } from '../../icons';
import ActiveOverlay from '../../components/ActiveOverlay';
import BottomBarMobile from '../BottomBarMobile';
import MobilePlaybackControls from '../MobilePlaybackControls';
import PlayControl from '../../components/PlayControl';
import UnmuteButton from '../../components/UnmuteButton';
import SettingsOverlay from '../SettingsOverlay';
import Header from '../Header';
import SubtitleText from '../../components/SubtitleText';
import AutoplayOverlay from '../AutoplayOverlay';
import ModalVideoData from '../../containers/ModalVideoData';
import styles from './styles.module.scss';

export interface IPlayerProps {
  posterImgUrl?: string;
}

const Player = ({ posterImgUrl }) => {
  useLoadUserPreferences();
  usePlayerIntervals();
  useCustomMessageError();

  const { stats } = useStats();
  const { togglePlay, paused, isMuted, unmuted } = usePlayback();
  const {
    toggleFullscreen,
    hideSettings,
    toggleUserActive,
    isUserActive,
    fullscreen: isFullscreen,
    settingsVisible,
  } = useUI();
  const { showStats, setHideStats, setShowStats } = useStats();
  const { playerData } = usePlayerData();
  const playerParams = usePlayerParams();
  const { isContextMenuVisible, contextMenuPosition, hideContextMenu } = useContextMenu();
  const { error } = usePlayerError();
  const { t } = useTranslation('common');
  const { subtitleEnabled } = useSubtitle();
  const { currentCueText } = useTextTrack();
  const { isAutoplayVisible, hideAutoplay } = useAutoplay();
  const firstPlay = useSelector(SELECTOR_PLAYBACK_FIRST_PLAY);
  const isActiveOverlayVisible = !isAutoplayVisible && (isUserActive || paused || showStats || settingsVisible);
  const isMobile = detectAndroid() || detectIOS();

  return (
    <div
      data-vjs-player
      data-resolution={stats.media.mediaResolution}
      onClick={togglePlay}
      onTouchStart={toggleUserActive}
      onDoubleClick={toggleFullscreen}
    >
      <PlayerMarkup playerData={playerData} />

      <IgnorePlayerEvents>
        {!firstPlay && <PlayControl className={styles.bigPlayButton} paused={true} onClick={togglePlay} />}
        {isAutoplayVisible && <AutoplayOverlay onHide={hideAutoplay} />}
        {error && <OverlayError error={error} />}
        <ContextMenu visible={isContextMenuVisible} position={contextMenuPosition} onHide={hideContextMenu}>
          <ContextMenuItem
            icon={<StatsIcon />}
            text={playerData?.audio ? t('context_menu.audio_data') : t('context_menu.video_data')}
            onClick={() => {
              hideContextMenu();
              setShowStats();
            }}
          />
          <ContextMenuItem
            icon={<ReloadIcon />}
            text={playerData?.audio ? t('context_menu.reload_audio') : t('context_menu.reload_video')}
            onClick={reloadPage}
          />
        </ContextMenu>
      </IgnorePlayerEvents>

      {posterImgUrl && <Poster src={posterImgUrl} />}
      <ActiveOverlay visible={!firstPlay || isActiveOverlayVisible}>
        {firstPlay && <Header title={playerParams.cur} />}
        {firstPlay && (isMobile ? <BottomBarMobile /> : <BottomBarDesktop />)}
        {firstPlay && isMobile && <MobilePlaybackControls size={isFullscreen ? IconSize.large : IconSize.medium} />}
        <UnmuteButton visible={isMuted} onClick={unmuted} />
        <SettingsOverlay visible={settingsVisible} onHide={hideSettings} />
        {showStats && <ModalVideoData onHide={setHideStats} />}
      </ActiveOverlay>
      <LoadingSpinner isAudio={playerData.audio} />

      {subtitleEnabled && currentCueText && (
        <SubtitleText
          className={classNames({ [styles.bottomBarOffset]: isActiveOverlayVisible, [styles.offsetMobile]: isMobile })}
          currentText={currentCueText}
        />
      )}
    </div>
  );
};

export default Player;
